<template>
  <AppSheet
    mode="sheet"
    :is-open="isOpen"
    :breakpoints="[0, sheetHeight]"
    :initial-breakpoint="sheetHeight"
    data-testid="paywall-dismiss-reason"
    @update:is-open="isOpen = $event"
    @did-dismiss="onDismissed"
  >
    <AppContent>
      <AppModalHeader
        :title="t('paywall.dismiss_reason.title')"
        :subtitle="t('paywall.dismiss_reason.subtitle')"
        icon="message-circle-question"
        wrap-title
      />

      <div class="px-5 pb-6 pt-1 flex flex-col gap-2">
        <!-- Style secondaire volontaire : le coral reste au CTA principal, et
             ici il n'y a pas de bonne réponse à pousser. -->
        <button
          v-for="reason in reasons"
          :key="reason"
          type="button"
          :data-testid="`paywall-reason-${reason}`"
          class="w-full rounded-2xl border border-line bg-white px-4 py-3 text-left text-[15px] font-medium text-ink transition active:bg-warm-2 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-strong"
          @click="onChoose(reason)"
        >
          {{ t(`paywall.dismiss_reason.${reason}`) }}
        </button>

        <!-- Sortie sans répondre, lisible mais pas concurrente des six autres. -->
        <button
          type="button"
          data-testid="paywall-reason-skip"
          class="mt-1 self-center rounded-full px-4 py-2.5 text-[14px] font-medium text-ink-3 transition active:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-strong"
          @click="onSkip"
        >
          {{ t('paywall.dismiss_reason.skip') }}
        </button>
      </div>
    </AppContent>
  </AppSheet>

  <TechnicalIssueFollowup />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppContent from '@/components/common/AppContent.vue'
import { useI18n } from 'vue-i18n'
import AppSheet from '@/components/common/AppSheet.vue'
import AppModalHeader from '@/components/common/AppModalHeader.vue'
import TechnicalIssueFollowup from '@/components/paywall/TechnicalIssueFollowup.vue'
import { usePaywallDismissReason, type PaywallDismissReason } from '@/composables/usePaywallDismissReason'
import { useHaptics } from '@/composables/useHaptics'

const { t } = useI18n({ useScope: 'global' })
const { isOpen, reasons, choose, skip, onDismissed } = usePaywallDismissReason()
const haptics = useHaptics()

/**
 * Le contenu fait à peu près 520 px : six lignes, un en-tête et une sortie.
 * Exprimé en fraction du viewport comme l'exige Ionic, mais calculé à partir
 * de cette hauteur réelle — une fraction fixe donnerait un demi-écran vide sur
 * un iPhone 17 et couperait la sixième raison sur un iPhone SE. Le plafond à
 * 0,92 garde le paywall visible derrière, le plancher évite la feuille écrasée
 * en paysage. L'ion-content scrolle si la police système est agrandie.
 */
const sheetHeight = computed(() => {
  const viewport = typeof window !== 'undefined' ? window.innerHeight : 800
  if (!viewport) return 0.8
  return Math.min(0.92, Math.max(0.5, 520 / viewport))
})

function onChoose(reason: PaywallDismissReason) {
  void haptics.impactLight()
  choose(reason)
}

function onSkip() {
  skip()
}

/*
 * Balayage vers le bas et tap sur le fond ne passent PAS par `skip()` : ils
 * n'ont rien à remettre à zéro, et `skip()` appelé ici effacerait la raison
 * qu'on vient de toucher (Ionic émet `update:isOpen` avant `did-dismiss`,
 * donc APRÈS le tap). Sans raison touchée, `chosenReason` vaut déjà null et
 * `did-dismiss` loggue la même chose que « Passer ». C'est le test 4.
 */
</script>
